import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Activity,
  BarChart3,
  Settings,
  Menu,
  X,
  Brain,
  LogOut,
  Shield,
  HelpCircle,
  Bot,
  Database,
  LifeBuoy,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { SystemStatusBar } from '@/components/layout/SystemStatusBar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { log } from '@/lib/logger';

interface AppLayoutProps {
  children: ReactNode;
}

const ADMIN_EMAIL = 'artachobruno@gmail.com';

const baseNavItems = [
  { path: '/today', labelKey: 'nav.today', icon: LayoutDashboard },
  { path: '/schedule', labelKey: 'nav.schedule', icon: Calendar },
  { path: '/plan', labelKey: 'nav.plan', icon: ClipboardList },
  { path: '/history', labelKey: 'nav.history', icon: Activity },
  { path: '/insights', labelKey: 'nav.insights', icon: BarChart3 },
  { path: '/coach', labelKey: 'nav.coach', icon: Brain },
  { path: '/profile', labelKey: 'nav.profile', icon: User },
  { path: '/settings', labelKey: 'nav.settings', icon: Settings },
];

const adminNavItems = [
  { path: '/admin', labelKey: 'nav.adminOps', icon: Shield },
  { path: '/admin/ai', labelKey: 'nav.adminAI', icon: Bot },
  { path: '/admin/analytics', labelKey: 'nav.adminAnalytics', icon: Database },
  { path: '/admin/tracing', labelKey: 'nav.tracing', icon: Activity },
];

const footerItems = [
  { path: '/privacy', labelKey: 'nav.privacy', icon: Shield },
  { path: '/faq', labelKey: 'nav.faq', icon: HelpCircle },
  { path: '/science', labelKey: 'nav.science', icon: Brain },
  { path: '/support', labelKey: 'nav.support', icon: LifeBuoy },
];

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { logout, user } = useAuth();
  const { t } = useTranslation();

  const isAdmin = user?.email === ADMIN_EMAIL;
  const navItems = isAdmin ? [...baseNavItems.slice(0, 6), ...adminNavItems, ...baseNavItems.slice(6)] : baseNavItems;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      log.error('LAYOUT', 'Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  // Desktop sidebar width: collapsed = 4rem (64px), expanded = 15rem (240px)
  const sidebarWidth = sidebarExpanded ? 'w-60' : 'w-16';
  const mainPadding = sidebarExpanded ? 'lg:pl-60' : 'lg:pl-16';

  return (
    <div className="min-h-[100svh]">
      {/* Mobile header */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 border-b border-white/[0.06] z-50 flex items-center justify-between px-4 pt-[env(safe-area-inset-top,0px)] h-[calc(3.5rem+env(safe-area-inset-top,0px))]"
        style={{
          background: 'rgba(11, 14, 20, 0.92)',
          backdropFilter: 'blur(16px)',
        }}
      >
        <Logo size="nav" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-slate-400 hover:text-slate-200"
        >
          {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </header>

      {/* System Status Bar - below mobile header */}
      <SystemStatusBar />

      {/* Sidebar - Slim telemetry nav */}
      <aside
        className={cn(
           'fixed left-0 border-r border-white/[0.06] z-40 transition-all duration-200 ease-in-out overflow-hidden',
           'top-[calc(3.5rem+env(safe-area-inset-top,0px))]',
           // Mobile: height stops above bottom nav bar
           'h-[calc(100svh-3.5rem-3.5rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))]',
           'lg:top-0 lg:h-full',
           sidebarWidth,
           'lg:translate-x-0',
           sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full',
         )}
         style={{
           background: 'rgba(11, 14, 20, 0.95)',
           backdropFilter: 'blur(16px)',
         }}
         onMouseEnter={() => setSidebarExpanded(true)}
         onMouseLeave={() => setSidebarExpanded(false)}
       >
         <div className="flex flex-col h-full overflow-y-auto overscroll-contain">
          {/* Logo - desktop only */}
          <div className={cn(
            'hidden lg:flex h-14 items-center border-b border-white/[0.06] transition-all duration-200',
            sidebarExpanded ? 'px-5' : 'justify-center px-2',
          )}>
            {sidebarExpanded ? <Logo size="nav" /> : (
              <div className="w-7 h-7 rounded bg-slate-700/50 flex items-center justify-center">
                <span className="text-[10px] font-mono font-bold text-slate-400 tracking-tighter">AS</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-3 px-2 space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={!sidebarExpanded ? t(item.labelKey) : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-md text-[11px] font-mono uppercase tracking-[0.08em] transition-all duration-150',
                    sidebarExpanded ? 'px-3 py-2' : 'justify-center px-2 py-2.5',
                    // Mobile always shows labels
                    'max-lg:px-3 max-lg:justify-start',
                    isActive
                      ? 'text-slate-200 bg-white/[0.06]'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.03]'
                  )}
                >
                  <Icon className={cn(
                    'flex-shrink-0 transition-colors',
                    isActive ? 'h-4 w-4' : 'h-3.5 w-3.5',
                  )} strokeWidth={isActive ? 1.8 : 1.2} />
                  {/* Label: always visible on mobile, animated on desktop */}
                  <span className={cn(
                    'whitespace-nowrap transition-opacity duration-150',
                    sidebarExpanded ? 'opacity-100' : 'lg:hidden opacity-100',
                  )}>
                    {t(item.labelKey)}
                  </span>
                  {/* Active indicator line */}
                  {isActive && !sidebarExpanded && (
                    <div className="hidden lg:block absolute left-0 w-0.5 h-4 bg-accent rounded-r" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div
            className="border-t border-white/[0.06] py-2 px-2 space-y-0.5"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
          >
            {footerItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={!sidebarExpanded ? t(item.labelKey) : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-md text-[10px] font-mono uppercase tracking-[0.08em] transition-all duration-150',
                    sidebarExpanded ? 'px-3 py-1.5' : 'justify-center px-2 py-2',
                    'max-lg:px-3 max-lg:justify-start',
                    location.pathname === item.path
                      ? 'text-slate-300 bg-white/[0.06]'
                      : 'text-slate-600 hover:text-slate-400 hover:bg-white/[0.03]'
                  )}
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.2} />
                  <span className={cn(
                    'whitespace-nowrap transition-opacity duration-150',
                    sidebarExpanded ? 'opacity-100' : 'lg:hidden opacity-100',
                  )}>
                    {t(item.labelKey)}
                  </span>
                </Link>
              );
            })}
            <button
              onClick={handleLogout}
              className={cn(
                'flex items-center gap-3 rounded-md text-[10px] font-mono uppercase tracking-[0.08em] transition-all duration-150 w-full',
                sidebarExpanded ? 'px-3 py-1.5' : 'justify-center px-2 py-2',
                'max-lg:px-3 max-lg:justify-start',
                'text-slate-600 hover:text-slate-400 hover:bg-white/[0.03]'
              )}
            >
              <LogOut className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.2} />
              <span className={cn(
                'whitespace-nowrap transition-opacity duration-150',
                sidebarExpanded ? 'opacity-100' : 'lg:hidden opacity-100',
              )}>
                {t('nav.logout')}
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main
        className={cn(
          'min-h-[100svh] transition-all duration-200',
          // Mobile: top padding = header + status bar, bottom padding = bottom nav
          'pt-[calc(3.5rem+1.5rem+env(safe-area-inset-top,0px))] pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]',
          'lg:pt-0 lg:pb-0',
          mainPadding,
        )}
        style={{
          background: 'var(--app-bg)',
        }}
      >
        <div className="mx-auto max-w-[1400px] px-4 py-4 lg:px-6 lg:py-6">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Tab Bar */}
      <MobileBottomNav />
    </div>
  );
}
